import type { ErrorKind } from './error-kind'

// A leaf: this file imports only the failure kinds, so any owner can use the vocabulary without depending on a feature.

/**
 * What an intent answers. It never throws a status and never reports through a side channel.
 *
 * `superseded` is its own answer and not an error kind: a newer request replaced this one before it settled, so it
 * neither worked nor failed, and a caller must not report it as a fault. It stays out of ErrorKind because the
 * kinds say why a load failed.
 */
export type IntentResult<T> =
    | { ok: true; value: T }
    | { ok: false; kind: ErrorKind; error?: unknown; superseded?: undefined }
    | { ok: false; superseded: true }

export interface Intent<I, R> {
    /** Dotted and flat under its owner, such as `curator.enable`. */
    id: `${string}.${string}`
    /** True when the operation cannot be undone. The UI asks first; the owner does not. */
    destructive?: boolean
    /** Checked against what the owner already knows. A refusal is returned, never queued. */
    precondition: (input: I) => IntentResult<void>
    perform: (input: I) => Promise<IntentResult<R>>
}

export const allowed: IntentResult<void> = { ok: true, value: undefined }
export const refused = (kind: ErrorKind): IntentResult<void> => ({ ok: false, kind })
